import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Flame, Check, X, CalendarDays } from "lucide-react";
import { getSlots, createBooking } from "../lib/api";

function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const CALENDAR_CLASS_NAMES = {
  day_selected: "bg-[#CCFF00] text-black hover:bg-[#CCFF00] hover:text-black focus:bg-[#CCFF00]",
  day_today: "text-[#CCFF00] font-bold",
  head_cell: "text-white/40 w-9 font-bold text-[0.7rem] uppercase tracking-widest",
  cell: "h-9 w-9 text-center text-sm relative p-0",
  day: "h-9 w-9 p-0 font-medium hover:bg-white/5 text-white",
  nav_button: "h-7 w-7 bg-transparent text-white/70 hover:text-[#CCFF00] hover:bg-transparent border-0",
  caption_label: "font-display text-lg uppercase tracking-tight text-white",
};

export default function BookSlot() {
  const [date, setDate] = useState(todayISO());
  const dateStr = useMemo(() => toIsoDate(date), [date]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getSlots(dateStr);
      setSlots(data);
    } catch (e) {
      toast.error("Failed to load slots");
    } finally {
      setLoading(false);
    }
  }, [dateStr]);

  useEffect(() => {
    load();
  }, [load]);

  // Light polling for real-time feel
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const onPick = (slot) => {
    if (slot.is_booked) return;
    setSelected(slot);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await createBooking({
        ...form,
        email: form.email.trim() || null,
        date: dateStr,
        hour: selected.hour,
      });
      setConfirmation(data);
      setOpen(false);
      setForm({ name: "", phone: "", email: "" });
      setSelected(null);
      await load();
      toast.success("Slot booked! See confirmation.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Booking failed";
      toast.error(typeof msg === "string" ? msg : "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  const availableCount = slots.filter((s) => !s.is_booked).length;

  return (
    <div data-testid="book-page" className="min-h-screen bg-[#0A0A0A] pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-3">Book a slot</p>
            <h1 className="font-display text-5xl lg:text-7xl uppercase tracking-tighter leading-none">
              Pick your hour
            </h1>
            <p className="text-white/60 mt-4 max-w-xl">
              Choose a date, tap an available slot. 18 hourly slots from 6 AM to 12 AM.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-display text-4xl text-[#CCFF00]" data-testid="available-count">{availableCount}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Available</div>
            </div>
            <button
              data-testid="refresh-slots-btn"
              onClick={load}
              disabled={loading}
              className="p-3 border border-white/15 hover:border-[#CCFF00] transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Calendar */}
          <aside className="lg:col-span-4">
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays className="w-4 h-4 text-[#CCFF00]" strokeWidth={1.8} />
                <span className="text-xs uppercase tracking-[0.3em] text-white/70">Select Date</span>
              </div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                disabled={(d) => d < todayISO()}
                data-testid="date-picker"
                className="bg-transparent text-white"
                classNames={CALENDAR_CLASS_NAMES}
              />
              <div className="mt-6 pt-6 border-t border-white/10 space-y-3 text-xs">
                <div className="flex items-center gap-3">
                  <span className="w-4 h-4 bg-[#1A1A1A] border border-white/10" />
                  <span className="text-white/60 uppercase tracking-widest">Available</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-4 h-4 bg-[#CCFF00]" />
                  <span className="text-white/60 uppercase tracking-widest">Selected</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-4 h-4 bg-[#27272A] opacity-60" />
                  <span className="text-white/60 uppercase tracking-widest">Booked</span>
                </div>
                <div className="flex items-center gap-3">
                  <Flame className="w-4 h-4 text-[#FF9F0A]" strokeWidth={1.8} />
                  <span className="text-white/60 uppercase tracking-widest">Peak hour</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Slots */}
          <main className="lg:col-span-8">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/50">Slots for</p>
                <p data-testid="selected-date" className="font-display text-3xl uppercase tracking-tight">
                  {date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {slots.map((s) => {
                const isSel = selected && selected.hour === s.hour;
                const cls = [
                  "slot-card p-5 rounded-sm relative",
                  s.is_booked ? "is-booked" : "",
                  isSel ? "is-selected" : "",
                ].join(" ");
                return (
                  <button
                    key={s.hour}
                    onClick={() => onPick(s)}
                    disabled={s.is_booked}
                    data-testid={`slot-${s.hour}-${s.is_booked ? "booked" : "available"}`}
                    className={cls}
                  >
                    {s.is_peak && !s.is_booked && (
                      <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#FF9F0A]">
                        <Flame className="w-3 h-3" strokeWidth={2} /> Peak
                      </span>
                    )}
                    {s.is_booked && (
                      <span className="absolute top-2 right-2 inline-flex items-center text-[9px] uppercase tracking-widest text-white/40">
                        Booked
                      </span>
                    )}
                    <div className="flex flex-col items-start text-left">
                      <span className="font-display text-3xl tracking-tight">
                        {s.hour_label}
                      </span>
                      <span className={`text-xs uppercase tracking-[0.2em] mt-2 ${isSel ? "text-black/70" : s.is_booked ? "text-white/30" : "text-white/50"}`}>
                        ₹{s.price}{s.is_peak ? " · Peak" : ""}
                      </span>
                    </div>
                  </button>
                );
              })}
              {slots.length === 0 && !loading && (
                <div className="col-span-full text-white/50 text-sm">No slots.</div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Booking dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="booking-dialog"
          className="bg-[#121212] border border-white/10 text-white rounded-none max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-3xl uppercase tracking-tight">
              Confirm booking
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {selected && (
                <>
                  {date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} ·{" "}
                  <span className="text-[#CCFF00]">{selected.hour_label}</span> · ₹{selected.price}
                  {selected.is_peak ? " (Peak)" : ""}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.25em] text-white/60">Name</Label>
              <Input
                data-testid="form-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="bg-[#0A0A0A] border-white/20 rounded-none text-white focus:border-[#CCFF00]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.25em] text-white/60">Phone</Label>
              <Input
                data-testid="form-phone"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="98xxxxxxxx"
                className="bg-[#0A0A0A] border-white/20 rounded-none text-white focus:border-[#CCFF00]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.25em] text-white/60">
                Email <span className="text-white/40 normal-case tracking-normal">(optional)</span>
              </Label>
              <Input
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@email.com — optional"
                className="bg-[#0A0A0A] border-white/20 rounded-none text-white focus:border-[#CCFF00]"
              />
            </div>
            <DialogFooter className="gap-3">
              <Button
                type="button"
                onClick={() => setOpen(false)}
                variant="ghost"
                data-testid="form-cancel"
                className="rounded-none border border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="form-submit"
                className="rounded-none bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#D4FF33]"
              >
                {submitting ? "Booking..." : "Confirm slot"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmation} onOpenChange={(o) => !o && setConfirmation(null)}>
        <DialogContent
          data-testid="confirmation-dialog"
          className="bg-[#121212] border border-[#CCFF00]/40 text-white rounded-none max-w-md"
        >
          {confirmation && (
            <>
              <DialogHeader>
                <div className="w-12 h-12 grid place-items-center bg-[#CCFF00] text-black mb-3">
                  <Check className="w-6 h-6" strokeWidth={2.5} />
                </div>
                <DialogTitle className="font-display text-3xl uppercase tracking-tight">
                  Slot locked in!
                </DialogTitle>
                <DialogDescription className="text-white/60">
                  Booking ID:{" "}
                  <span data-testid="confirmation-id" className="text-[#CCFF00] font-mono text-xs">
                    {confirmation.id}
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row label="Player" value={confirmation.name} />
                <Row label="Phone" value={confirmation.phone} />
                <Row label="Date" value={confirmation.date} />
                <Row label="Time" value={confirmation.hour_label} />
                <Row label="Price" value={`₹${confirmation.price}${confirmation.is_peak ? " (Peak)" : ""}`} />
                <Row label="Status" value={
                  <Badge className="bg-[#CCFF00] text-black uppercase tracking-widest rounded-none hover:bg-[#CCFF00]">{confirmation.status}</Badge>
                } />
              </div>
              <DialogFooter>
                <Button
                  data-testid="confirmation-done-btn"
                  onClick={() => setConfirmation(null)}
                  className="rounded-none bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#D4FF33]"
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center border-b border-white/5 py-2">
    <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</span>
    <span className="text-white text-sm">{value}</span>
  </div>
);
